// ============================================================
// useComponentMetric — Hook per tracciare navigazione utente
// tramite span OTLP. Ogni pagina diventa uno span figlio dello
// span radice di sessione, permettendo di ricostruire il
// journey completo in Grafana Tempo.
//
// Al mount invia view e path e apre lo span di pagina; all'unmount
// invia la durata di permanenza e chiude lo span.
//
// Uso: useComponentMetric("luogo", { fetchError, fetchErrorMessage, fallbackErrorMessage: "errore recupero luogo" });
//      // errori fuori dal fetch:
//      const metric = useComponentMetric("luogo");
//      metric.trackError("qualcosa è andato storto", error.stack);
// ============================================================

import { useEffect, useRef, useCallback } from "react";
import {
    trackSpanPageStart,
    trackSpanPageEnd,
    trackSpanPageError,
    trackComponentView,
    trackComponentDuration,
    trackComponentError,
    trackPagePath,
} from "./otelExporter";

export default function useComponentMetric(pageName, { fetchError, fetchErrorMessage, fallbackErrorMessage } = {}) {
    const hasTrackedRef = useRef(false);

    const trackError = useCallback(
        (errorMessage, errorStack) => {
            const message = errorMessage || "unknown";
            trackComponentError(pageName, message, errorStack);
            trackSpanPageError(pageName, message, errorStack);
        },
        [pageName]
    );

    useEffect(() => {
        // Registra lo span pagina sullo stack (null se non c'e' un contesto di sessione attivo)
        const pageSpanId = trackSpanPageStart(pageName);

        // Tempo di permanenza dell'utente sulla pagina: parte sempre al mount,
        // indipendentemente dallo span Tempo (che richiede un contesto attivo).
        const mountedAt = Date.now();

        // Il ref evita view/path doppi quando React riesegue l'effect (StrictMode)
        if (!hasTrackedRef.current) {
            hasTrackedRef.current = true;
            trackComponentView(pageName);
            trackPagePath(pageName);
        }

        return () => {
            trackComponentDuration(pageName, Date.now() - mountedAt);
            if (pageSpanId) trackSpanPageEnd(pageSpanId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fetchError) trackError(fetchErrorMessage || fallbackErrorMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchError]);

    return { trackError };
}
